import { db, doc, onSnapshot, secureGetDoc, secureSetDoc } from '../firebase';

export interface QRPaymentConfig {
  merchantCode: string;
  name: string;
  billerId: string;
  promptPayId: string;
  webhookUrl: string;
  webhookSecret: string;
  qrMode: 'DYNAMIC' | 'STATIC';
  qrExpiryMinutes: number;
  feePercentage: number;
  isActive: boolean;
  updatedAt?: string;
}

export const DEFAULT_QR_CONFIG: QRPaymentConfig = {
  merchantCode: 'QV353070BI06881536X',
  name: 'Giffarine Online Official Store',
  billerId: '010753700088205',
  promptPayId: '0939678649',
  webhookUrl: '/api/payment/webhook',
  webhookSecret: 'whsec_8f912c90432bc194a03e198b',
  qrMode: 'DYNAMIC',
  qrExpiryMinutes: 15,
  feePercentage: 0.0,
  isActive: true,
  updatedAt: new Date().toISOString()
};

const STORAGE_KEY = 'qr_payment_config';
const EVENT_KEY = 'qr-payment-config-updated';

export function getCachedQrPaymentConfig(): QRPaymentConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return { ...DEFAULT_QR_CONFIG, ...JSON.parse(raw) };
    }
  } catch (e) {
    console.error('Error reading QR payment config from localStorage:', e);
  }
  return { ...DEFAULT_QR_CONFIG };
}

export async function fetchQrPaymentConfig(): Promise<QRPaymentConfig> {
  try {
    // 1. Try Backend API
    const res = await fetch('/api/payment/config');
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.merchant) {
        const config: QRPaymentConfig = {
          merchantCode: data.merchant.merchantCode || DEFAULT_QR_CONFIG.merchantCode,
          name: data.merchant.name || DEFAULT_QR_CONFIG.name,
          billerId: data.merchant.billerId || DEFAULT_QR_CONFIG.billerId,
          promptPayId: data.merchant.promptPayId || DEFAULT_QR_CONFIG.promptPayId,
          webhookUrl: data.merchant.webhookUrl || DEFAULT_QR_CONFIG.webhookUrl,
          webhookSecret: data.merchant.webhookSecret || DEFAULT_QR_CONFIG.webhookSecret,
          qrMode: data.merchant.qrMode || DEFAULT_QR_CONFIG.qrMode,
          qrExpiryMinutes: data.merchant.qrExpiryMinutes || DEFAULT_QR_CONFIG.qrExpiryMinutes,
          feePercentage: data.merchant.feePercentage || 0,
          isActive: data.merchant.isActive !== false,
          updatedAt: data.merchant.updatedAt || new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        return config;
      }
    }

    // 2. Try Firestore fallback
    const snap = await secureGetDoc(doc(db, 'site_settings', 'qr_payment_config'), 'site_settings/qr_payment_config');
    if (snap && snap.exists()) {
      const data = snap.data() as any;
      const config = { ...DEFAULT_QR_CONFIG, ...data };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      return config;
    }
  } catch (err) {
    console.error('Failed fetching QR payment config:', err);
  }

  return getCachedQrPaymentConfig();
}

export async function saveQrPaymentConfig(config: Partial<QRPaymentConfig>): Promise<boolean> {
  try {
    const current = getCachedQrPaymentConfig();
    const updated: QRPaymentConfig = {
      ...current,
      ...config,
      updatedAt: new Date().toISOString()
    };

    // 1. Save to localStorage immediately
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent(EVENT_KEY, { detail: updated }));

    // 2. Save to Backend API
    try {
      const res = await fetch('/api/payment/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
      if (!res.ok) {
        console.warn('Backend API returned non-ok status for QR config:', res.status);
      }
    } catch (apiErr) {
      console.warn('Backend API QR config save warning:', apiErr);
    }

    // 3. Save to Firestore (gracefully caught if unauthenticated/offline)
    try {
      await secureSetDoc(
        doc(db, 'site_settings', 'qr_payment_config'),
        updated,
        'site_settings/qr_payment_config'
      );
    } catch (fsErr) {
      console.warn('Firestore QR config save warning:', fsErr);
    }

    return true;
  } catch (err) {
    console.error('Failed saving QR payment config:', err);
    return false;
  }
}

export function subscribeQrPaymentConfig(onUpdate: (config: QRPaymentConfig) => void): () => void {
  onUpdate(getCachedQrPaymentConfig());

  let unsubFirestore: (() => void) | null = null;
  try {
    unsubFirestore = onSnapshot(
      doc(db, 'site_settings', 'qr_payment_config'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as any;
          const config = { ...DEFAULT_QR_CONFIG, ...data };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
          onUpdate(config);
        }
      },
      (err) => console.warn('Firestore QR config snapshot error:', err)
    );
  } catch (e) {}

  const handleCustomEvent = (e: Event) => {
    const evt = e as CustomEvent<QRPaymentConfig>;
    if (evt.detail) onUpdate(evt.detail);
  };

  window.addEventListener(EVENT_KEY, handleCustomEvent);

  fetchQrPaymentConfig().then(c => onUpdate(c));

  return () => {
    if (unsubFirestore) unsubFirestore();
    window.removeEventListener(EVENT_KEY, handleCustomEvent);
  };
}
